/**
 * Logger utility for consistent logging throughout the application
 * This allows us to:
 * 1. Easily toggle logs off in production
 * 2. Add consistent formatting to all logs
 * 3. Potentially integrate with error tracking services
 */

// Determine if we're in development mode
const isDev = import.meta.env.DEV;

// Create different log levels
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogOptions {
  module?: string;
  data?: any;
}

/**
 * Main logging function that handles all log levels
 */
function logMessage(level: LogLevel, message: string, options: LogOptions = {}) {
  // In production, only show warnings and errors
  if (!isDev && (level === 'debug' || level === 'info')) {
    return;
  }

  const { module, data } = options;
  const timestamp = new Date().toISOString();
  const prefix = module ? `[${module}]` : '';
  
  switch (level) {
    case 'debug':
      console.debug(`${timestamp} ${prefix} ${message}`, data || '');
      break;
    case 'info':
      console.info(`${timestamp} ${prefix} ${message}`, data || '');
      break;
    case 'warn':
      console.warn(`${timestamp} ${prefix} ${message}`, data || '');
      break;
    case 'error':
      console.error(`${timestamp} ${prefix} ${message}`, data || '');
      break;
  }
}

// Export convenience methods
export const logger = {
  debug: (message: string, options?: LogOptions) => logMessage('debug', message, options),
  info: (message: string, options?: LogOptions) => logMessage('info', message, options),
  warn: (message: string, options?: LogOptions) => logMessage('warn', message, options),
  error: (message: string, options?: LogOptions) => logMessage('error', message, options),
};

export default logger; 